import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Card,
  CardContent,
  CardActions,
  Button,
  Link,
  Grid,
  Divider,
  TablePagination,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Search, Phone, WhatsApp, Email, Facebook, Instagram, Public, Place, Paid, Map, Navigation } from "@mui/icons-material";
import { IProveedor } from "../models/codes";
import lsApi from "../api/lsApi";

function normalizeServices(text?: string): string[] {
  if (!text) return [];
  
  // Extraer hashtags del texto
  const hashtags = text.match(/#[\wáéíóúñÁÉÍÓÚÑ]+/g);
  
  if (!hashtags) return [];
  
  // Limpiar hashtags: remover el #, convertir a minúsculas y eliminar duplicados
  return hashtags
    .map(tag => tag.substring(1).toLowerCase().trim())
    .filter(Boolean)
    .filter((tag, idx, arr) => arr.indexOf(tag) === idx);
}

function getAllServices(proveedores: IProveedor[]): string[] {
  const set = new Set<string>();
  proveedores.forEach(p => {
    normalizeServices(p.servicios).forEach(service => set.add(service));
  });
  // Retornar array ordenado alfabéticamente
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
}

function onlyDigits(input?: string): string {
  return (input || "").replace(/\D+/g, "");
}

const mockData: IProveedor[] = [
  {
    idProveedor: 1,
    nombreContacto: "Soporte Diceros",
    nombreComercial: "Diceros",
    nit: "123",
    dpi: "CF",
    direccion: "Zona 2, Guatemala",
    telefono: "+5023941408",
    celular1: "+502123456",
    correo: "condominio@ls-sys.com",
    linkFb: "https://www.facebook.com/dicerosgt",
    linkIg: "https://www.instagram.com/dicerosgt/",
    linkTt: "https://www.tiktok.com/@dicerosgt",
    puntuacion: 5,
    costoEnvio: "Sin Costo",
    cobertura: "Toda Guatemala",
    servicios: "#software #condominio #desarrollo #tecnología",
    estado: 1,
  },
];

export const Proveedores = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [providers, setProviders] = useState<IProveedor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(9);

  useEffect(() => {
    const fetchProveedores = async () => {
      try {
        setLoading(true);
        const response = await lsApi.get('/api/ControlAcceso/ListadoProveedores');
        setProviders(response.data);
        setError("");
      } catch (error) {
        console.error('Error al obtener proveedores:', error);
        setError('No se pudieron cargar los proveedores. Intenta nuevamente.');
        // Fallback a datos mock en caso de error
        setProviders(mockData);
      } finally {
        setLoading(false);
      }
    };

    fetchProveedores();
  }, []);

  const availableTags = useMemo(() => getAllServices(providers), [providers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return providers.filter(p => {
      const services = normalizeServices(p.servicios);
      const matchesTags = selectedTags.length === 0 || services.some(s => selectedTags.includes(s));

      if (!matchesTags) return false;

      if (!q) return true;
      const values = [
        p.nombreComercial,
        p.nombreContacto,
        p.dpi,
        p.nit,
        p.direccion,
        p.cobertura,
        p.correo,
        p.telefono,
        p.celular1,
        p.celular2,
        ...(services || []),
      ];
      return values.some(v => String(v ?? '').toLowerCase().includes(q));
    });
  }, [providers, search, selectedTags]);

  useEffect(() => {
    setPage(0);
  }, [search, selectedTags]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Box textAlign="center" display="flex" alignItems="center" flexDirection="column">
        <Typography variant="h4" component="div" gutterBottom marginY="1em">
          Proveedores
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 2, maxWidth: 1000 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
        <Box display="flex" flexDirection="column" alignItems="center" width={1} maxWidth={1000} sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ width: '100%', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Buscar proveedores"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              fullWidth
              sx={{ maxWidth: 520, width: '100%', mx: 'auto', px: { xs: 1, sm: 0 } }}
            />
          </Box>

          {availableTags.length > 0 && (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: '100%', mb: 2, justifyContent: 'center' }}>
              {availableTags.map(service => (
                <Chip
                  key={service}
                  label={service}
                  clickable
                  color={selectedTags.includes(service) ? 'primary' : 'default'}
                  variant={selectedTags.includes(service) ? 'filled' : 'outlined'}
                  onClick={() => toggleTag(service)}
                  size="small"
                />
              ))}
              {selectedTags.length > 0 && (
                <Chip label="Limpiar filtros" color="secondary" onClick={() => setSelectedTags([])} size="small" />
              )}
            </Stack>
          )}

          <Grid container spacing={2} sx={{ width: '100%' }} justifyContent="center" alignItems="stretch">
            {paginated.map(p => {
              const services = normalizeServices(p.servicios);
              const phone = p.telefono || p.celular1 || p.celular2;
              const phoneDigits = onlyDigits(phone);
              const waDigits = phoneDigits;
              return (
                <Grid item xs={12} sm={6} md={4} key={p.idProveedor}>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      borderRadius: 2, 
                      border: 1, 
                      borderColor: 'divider', 
                      transition: 'box-shadow .2s, transform .2s', 
                      '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' } 
                    }}
                  >
                    <CardContent sx={{ pb: 1, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Nombre comercial y contacto */}
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {p.nombreComercial}
                        </Typography>
                        {p.nombreContacto && (
                          <Typography variant="body2" color="text.secondary">
                            {p.nombreContacto}
                          </Typography>
                        )}
                      </Box>

                      {/* Dirección */}
                      {p.direccion && (
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ flex: 1 }}>
                            <Place fontSize="small" color="action" sx={{ mt: 0.2 }} />
                            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                              {p.direccion}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Abrir en Google Maps" arrow>
                              <IconButton
                                size="small"
                                component={Link}
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.direccion)}`}
                                target="_blank"
                                rel="noopener"
                                sx={{ padding: 0.5 }}
                              >
                                <Map fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Abrir en Waze" arrow>
                              <IconButton
                                size="small"
                                component={Link}
                                href={`https://waze.com/ul?q=${encodeURIComponent(p.direccion)}`}
                                target="_blank"
                                rel="noopener"
                                sx={{ padding: 0.5 }}
                              >
                                <Navigation fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      )}

                      {/* Cobertura y Costo */}
                      {(p.cobertura || p.costoEnvio) && (
                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center">
                          {p.cobertura && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Cobertura:
                              </Typography>
                              <Typography variant="caption" color="text.primary">
                                {p.cobertura}
                              </Typography>
                            </Box>
                          )}
                          {p.costoEnvio && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Paid fontSize="small" color="action" />
                              <Typography variant="caption" color="text.primary">
                                {p.costoEnvio}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      )}

                      {/* Servicios/Tags */}
                      {services.length > 0 && (
                        <Box>
                          <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="flex-start">
                            {services.map(s => (
                              <Chip 
                                key={s} 
                                label={s} 
                                size="small" 
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: '22px'
                                }} 
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </CardContent>

                    {/* Acciones/Botones */}
                    <CardActions sx={{ mt: 'auto', p: 2, pt: 1, flexDirection: 'column', gap: 1, alignItems: 'stretch' }}>
                      {/* Línea 1: Teléfonos y Email */}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="space-between">
                        {phoneDigits && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<Phone fontSize="small" />} 
                            component={Link} 
                            href={`tel:${phoneDigits}`}
                            sx={{ minWidth: 'auto', px: 1.5 }}
                          >
                            Llamar
                          </Button>
                        )}
                        {waDigits && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<WhatsApp fontSize="small" />} 
                            component={Link} 
                            href={`https://wa.me/${waDigits}`} 
                            target="_blank" 
                            rel="noopener"
                            sx={{ minWidth: 'auto', px: 1.5 }}
                          >
                            WhatsApp
                          </Button>
                        )}
                        {p.correo && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<Email fontSize="small" />} 
                            component={Link} 
                            href={`mailto:${p.correo}`}
                            sx={{ minWidth: 'auto', px: 1.5 }}
                          >
                            Email
                          </Button>
                        )}
                      </Stack>

                      {/* Línea 2: Redes Sociales */}
                      {(p.linkFb || p.linkIg || p.linkTt) && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="space-between">
                          {p.linkFb && (
                            <Button 
                              size="small" 
                              variant="text" 
                              startIcon={<Facebook fontSize="small" />} 
                              component={Link} 
                              href={p.linkFb} 
                              target="_blank" 
                              rel="noopener"
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              Facebook
                            </Button>
                          )}
                          {p.linkIg && (
                            <Button 
                              size="small" 
                              variant="text" 
                              startIcon={<Instagram fontSize="small" />} 
                              component={Link} 
                              href={p.linkIg} 
                              target="_blank" 
                              rel="noopener"
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              Instagram
                            </Button>
                          )}
                          {p.linkTt && (
                            <Button 
                              size="small" 
                              variant="text" 
                              startIcon={<Public fontSize="small" />} 
                              component={Link} 
                              href={p.linkTt} 
                              target="_blank" 
                              rel="noopener"
                              sx={{ minWidth: 'auto', px: 1 }}
                            >
                              TikTok
                            </Button>
                          )}
                        </Stack>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
            {paginated.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">No hay proveedores para estos filtros.</Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[6, 9, 12, 24]}
            labelRowsPerPage="Tarjetas por página"
          />
        </Box>
        )}
      </Box>
    </>
  );
};
